import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Group,
  Text,
  Stack,
  Loader,
  Card,
  Avatar,
  Grid,
  Pagination,
  Button,
  Modal,
  Checkbox,
  Select,
  Divider,
  Box,
  Flex,
} from "@mantine/core";
import { IconSearch, IconUsers, IconUser } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import ShipperService from "services/shipper.service";
import { UserRequest } from "types/requestes.type";
import { GroupResponse, UserResponse } from "types/responses.type";
import UserPropertiesModal from "components/UserPropertiesModal";

const UserManagement = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRequest | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserResponse>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;
  const { user } = useSelector((state: RootState) => state.auth);

  const fetchUsers = () => {
    setLoading(true);
    ShipperService.getUsers()
      .then((res) => {
        if (res.ok) {
          setUsers(res.data ?? []);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = search
    ? users.filter(
        (u) =>
          u.displayName.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleUserClick = (user: UserResponse) => {
    setEditUser({ ...user } as any);
    setSelectedUser(user);
    setModalOpen(true);
  };

  const createBlankUser = (): UserResponse =>
    ({
      userId: undefined,
      displayName: "",
      email: "",
      type: "SHIPPER_USER",
    } as any);

  function handleUserSave(user: UserRequest): void {
    const sUsers = [...users];
    const idx = sUsers.findIndex((u) => u.userId === user.userId);
    if (idx < 0) {
      sUsers.push(user as any);
    } else {
      sUsers[idx] = { ...sUsers[idx], ...user } as any;
    }
    setUsers(sUsers);
  }

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>User Management</Title>
          <Button
            onClick={() => {
              setSelectedUser(createBlankUser());
            }}
            variant="filled"
          >
            New User
          </Button>
        </Group>
        <Group justify="space-between" align="center">
          <TextInput
            autoComplete="off"
            placeholder="Search users..."
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            w={300}
          />
          <Pagination
            total={Math.ceil(filteredUsers.length / itemsPerPage)}
            value={activePage}
            onChange={setActivePage}
          />
        </Group>
        <Divider my="sm" />
        {loading ? (
          <Loader />
        ) : users.length == 0 ? (
          <Stack align="center" justify="center" style={{ height: "50vh" }}>
            <IconUsers size={48} color="gray" />
            <Text ta="center" c="dimmed">
              No groups found.
            </Text>
          </Stack>
        ) : (
          <Grid>
            {filteredUsers.map((user) => (
              <Grid.Col key={user.userId} span={{ base: 12, lg: 4, md: 6 }}>
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  h={"100%"}
                  onClick={() => handleUserClick(user)}
                  style={{ cursor: "pointer" }}
                >
                  <Group>
                    <Avatar color="blue" radius="xl">
                      {user.displayName[0]}
                    </Avatar>
                    <Stack gap={0}>
                      <Text fw={500}>{user.displayName}</Text>
                      <Text size="sm" c="dimmed">
                        {user.email}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {user.type}
                      </Text>
                    </Stack>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>
      <UserPropertiesModal
        user={selectedUser}
        onClose={() => setSelectedUser(undefined)}
        onSave={handleUserSave}
        isSelfEdit={selectedUser?.userId === user?.userId}
      />
    </Container>
  );
};

export default UserManagement;
